import React, { useCallback, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import { SkeletonRows } from 'components/Skeleton'
import AddTaskRoundedIcon from '@mui/icons-material/AddTaskRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'
import { getRecommendedRemedials } from 'services/fileService'
import ActionsAPI from 'services/ActionsAPI'
import { refreshRemedialCounts } from 'features/actions/ducks/refreshRemedialCounts'
import ConvertToTaskDialog from 'features/actions/components/ConvertToTaskDialog'

const PAGE = 50

const SEVERITY_TINT = {
  critical: RAG_LEVELS.critical.fg,
  high: RAG_LEVELS.high.fg,
  medium: RAG_LEVELS.medium.fg,
  low: RAG_LEVELS.low.fg
}

const formatDate = iso => {
  if (!iso) return '-'
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const isOverdue = a => {
  if (a.status !== 'open' || !a.suggestedDueBy) return false
  return new Date(a.suggestedDueBy) < new Date()
}

const sourceLabel = a => {
  if (a.source === 'guru') return 'AI extracted'
  if (a.source === 'manual') return 'Manual entry'
  if (a.source === 'incident') return 'Incident'
  if (a.source === 'task_form') return 'Task form'
  return a.source || '-'
}

const SortTh = ({ label, sortKey, sort, onSort, style }) => {
  const active = sort?.key === sortKey
  return (
    <Th style={style} $sortable onClick={() => onSort(sortKey)} title={`Sort by ${label.toLowerCase()}`}>
      <SortLabel>
        {label}
        <Caret $active={active}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</Caret>
      </SortLabel>
    </Th>
  )
}

const RecommendedRemedialsPanel = ({ bp, canEdit, onCountChange, onOpenInsight }) => {
  const bpId = bp?.id
  const [review, setReview] = useState({ rows: [], meta: null, total: 0, unfilteredTotal: 0, loading: true, loadingMore: false })
  const [active, setActive] = useState({ rows: [], meta: null, total: 0, unfilteredTotal: 0, loading: true, loadingMore: false })
  const [busyId, setBusyId] = useState(null)
  const [convertTarget, setConvertTarget] = useState(null)
  const [severity, setSeverity] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null)

  const load = useCallback((status, page) => {
    if (!bpId) return
    const set = status === 'pending_review' ? setReview : setActive
    set(s => ({ ...s, loading: page === 1, loadingMore: page > 1 }))
    getRecommendedRemedials(bpId, {
      page,
      perPage: PAGE,
      status,
      severity: severity.length ? severity.join(',') : undefined,
      search: search || undefined,
      sort: sort?.key,
      dir: sort?.dir
    })
      .then(res => {
        const wanted = status === 'pending_review' ? 'pending_review' : 'open'
        const incoming = (res?.data?.data || []).filter(i => i.status === wanted)
        const m0 = res?.data?.meta || null
        const unfiltered = wanted === 'pending_review' ? (m0?.pendingReviewTotal ?? 0) : (m0?.openTotal ?? 0)
        set(s => ({
          rows: (m0?.currentPage ?? 1) > 1
            ? [...s.rows, ...incoming.filter(i => !s.rows.some(x => x.id === i.id))]
            : incoming,
          meta: m0,
          total: m0?.total ?? incoming.length,
          unfilteredTotal: unfiltered,
          loading: false,
          loadingMore: false
        }))
      })
      .catch(() => {
        set(s => ({ ...s, loading: false, loadingMore: false }))
        toast.error('Could not load remedials. Please try again.')
      })
  }, [bpId, severity, search, sort])

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => { load('pending_review', 1); load('open', 1) }, [load])

  const onSort = key => setSort(prev => (
    prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  ))

  useEffect(() => {
    if (!onCountChange) return
    if (!review.meta && !active.meta) return
    onCountChange('remedials', (review.unfilteredTotal ?? 0) + (active.unfilteredTotal ?? 0))
  }, [review.meta, active.meta, review.unfilteredTotal, active.unfilteredTotal, onCountChange])

  const dropRow = (status, id) => {
    const set = status === 'pending_review' ? setReview : setActive
    set(s => ({
      ...s,
      rows: s.rows.filter(r => r.id !== id),
      total: Math.max(0, (s.total || 1) - 1),
      unfilteredTotal: Math.max(0, (s.unfilteredTotal || 1) - 1)
    }))
  }

  const approve = (a) => {
    if (busyId) return
    setBusyId(a.id)
    ActionsAPI.approve(a.id)
      .then(() => {
        setReview(s => ({
          ...s,
          rows: s.rows.filter(r => r.id !== a.id),
          total: Math.max(0, (s.total || 1) - 1),
          unfilteredTotal: Math.max(0, (s.unfilteredTotal || 1) - 1)
        }))
        setActive(s => ({
          ...s,
          rows: [{ ...a, status: 'open' }, ...s.rows],
          total: (s.total || 0) + 1,
          unfilteredTotal: (s.unfilteredTotal || 0) + 1
        }))
        toast.success('Approved. It’s now an active remedial you can turn into a task.')
        refreshRemedialCounts()
      })
      .catch(() => toast.error('Could not approve this remedial. Please try again.'))
      .finally(() => setBusyId(null))
  }

  const dismiss = (a) => {
    if (busyId || a.status !== 'pending_review') return
    if (!window.confirm(`Is “${a.title}” not a real issue? This AI find will be removed and will not become a remedial. You can restore it later from the remedials list.`)) return
    setBusyId(a.id)
    ActionsAPI.dismiss(a.id, {})
      .then(() => { dropRow(a.status, a.id); toast.success('Removed as not a real issue.'); refreshRemedialCounts() })
      .catch(() => toast.error('This AI find could not be removed. Please try again.'))
      .finally(() => setBusyId(null))
  }

  const complete = (a) => {
    if (busyId) return
    setBusyId(a.id)
    ActionsAPI.update(a.id, { status: 'resolved' })
      .then(() => { dropRow('open', a.id); toast.success('Marked complete.'); refreshRemedialCounts() })
      .catch(() => toast.error('Could not update this remedial. Please try again.'))
      .finally(() => setBusyId(null))
  }

  const renderActions = (a, kind) => {
    if (!canEdit) return <Muted>-</Muted>
    const disabled = busyId === a.id
    if (kind === 'review') {
      return (
        <RowBtns>
          <RowBtn $tone='ok' disabled={disabled} onClick={() => approve(a)}>
            <ThumbUpAltRoundedIcon style={{ fontSize: 13 }} /> Approve
          </RowBtn>
          <RowBtn $tone='mute' disabled={disabled} onClick={() => dismiss(a)}>
            <BlockRoundedIcon style={{ fontSize: 13 }} /> Not a real issue
          </RowBtn>
        </RowBtns>
      )
    }
    return (
      <RowBtns>
        <RowBtn $tone='task' disabled={disabled} onClick={() => setConvertTarget(a)}>
          <AddTaskRoundedIcon style={{ fontSize: 14 }} /> Create task
        </RowBtn>
        <RowBtn $tone='ok' disabled={disabled} onClick={() => complete(a)}>
          <CheckRoundedIcon style={{ fontSize: 14 }} /> Mark as complete
        </RowBtn>
      </RowBtns>
    )
  }

  const renderTable = (list, kind) => (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <SortTh style={{ minWidth: 90 }} label='Severity' sortKey='severity' sort={sort} onSort={onSort} />
            <Th style={{ width: '42%' }}>Remedial</Th>
            <Th>Source</Th>
            <SortTh style={{ minWidth: 110 }} label='Due' sortKey='due' sort={sort} onSort={onSort} />
            <SortTh style={{ minWidth: 110 }} label={kind === 'review' ? 'Suggested' : 'Created'} sortKey='created' sort={sort} onSort={onSort} />
            <Th style={{ minWidth: kind === 'review' ? 180 : 260, textAlign: 'right' }}>Action</Th>
          </tr>
        </thead>
        <tbody>
          {list.map((a, i) => (
            <Row key={a.id} $stagger={i % 25}>
              <Td><SeverityPill $tint={SEVERITY_TINT[a.severity] || '#9aa0aa'}>{a.severity}</SeverityPill></Td>
              <Td>
                <RowTitle>{a.title}</RowTitle>
                {a.description && <RowDesc>{a.description}</RowDesc>}
              </Td>
              <Td>
                <SourceChip>{sourceLabel(a)}</SourceChip>
                {a.sourceFile?.displayName && <SourceFile as='button' type='button' title='View document insight' onClick={() => onOpenInsight?.({ id: a.sourceFile.id, displayName: a.sourceFile.displayName })}>{a.sourceFile.displayName}</SourceFile>}
              </Td>
              <Td>
                <DueCell $overdue={isOverdue(a)}>
                  {formatDate(a.suggestedDueBy)}
                  {isOverdue(a) && <OverdueMark>OVERDUE</OverdueMark>}
                </DueCell>
              </Td>
              <Td><Muted>{formatDate(a.createdAt)}</Muted></Td>
              <Td style={{ textAlign: 'right' }}>{renderActions(a, kind)}</Td>
            </Row>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  )

  const initialLoading = review.loading && active.loading && review.rows.length === 0 && active.rows.length === 0
  const reviewTotal = review.total
  const activeTotal = active.total
  const hasFilter = severity.length > 0 || !!search
  const nothing = !initialLoading && reviewTotal === 0 && activeTotal === 0
  const clearFilters = () => { setSeverity([]); setSearchInput('') }

  return (
    <div>
      <Sub>
        Remedials Guru has found across this building’s documents. <strong>To review</strong> are AI suggestions awaiting your decision:
        <strong> Approve</strong> to make one an active remedial, or choose <strong>Not a real issue</strong> to remove it. <strong>Active remedials</strong> are confirmed.
        Turn one into a <strong>task</strong> to get it fixed, or mark it <strong>complete</strong> once it has been put right.
        Creating a task needs Task Management enabled; if it isn’t, you’ll be shown how to switch it on.
      </Sub>

      {!initialLoading && (!nothing || hasFilter) && (
        <FilterBar>
          <SearchWrap>
            <SearchRoundedIcon style={{ fontSize: 16 }} />
            <SearchInput
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder='Search remedials…'
            />
            {searchInput && (
              <ClearSearch type='button' onClick={() => setSearchInput('')} aria-label='Clear search'>
                <CloseRoundedIcon style={{ fontSize: 15 }} />
              </ClearSearch>
            )}
          </SearchWrap>
          <SevChips>
            {['critical', 'high', 'medium', 'low'].map(sev => {
              const on = severity.includes(sev)
              return (
                <SevChip
                  key={sev}
                  type='button'
                  $tint={SEVERITY_TINT[sev]}
                  $on={on}
                  aria-pressed={on}
                  onClick={() => setSeverity(s => on ? s.filter(x => x !== sev) : [...s, sev])}
                >
                  {sev}
                </SevChip>
              )
            })}
            {hasFilter && <ClearAll type='button' onClick={clearFilters}>Clear</ClearAll>}
          </SevChips>
        </FilterBar>
      )}

      {initialLoading ? (
        <SkeletonRows rows={5} />
      ) : nothing ? (
        hasFilter ? (
          <EmptyState>No remedials match your filters. <ClearInline type='button' onClick={clearFilters}>Clear filters</ClearInline></EmptyState>
        ) : (
          <EmptyState>No remedials for this building yet. They appear here as Guru finds issues in your documents.</EmptyState>
        )
      ) : (
        <>
          {(reviewTotal > 0 || review.rows.length > 0) && (
            <Section>
              <SectionHead>
                To review<Count>{reviewTotal}</Count>
                <SectionHint>AI-suggested: approve to confirm, or mark as not a real issue</SectionHint>
              </SectionHead>
              {renderTable(review.rows, 'review')}
              <InfiniteScrollSentinel
                hasMore={!!review.meta && review.meta.currentPage < review.meta.lastPage}
                loading={review.loadingMore}
                onLoadMore={() => load('pending_review', (review.meta?.currentPage ?? 1) + 1)}
              />
              {review.rows.length < reviewTotal && (
                <ListTotal>Showing {review.rows.length} of {reviewTotal}</ListTotal>
              )}
            </Section>
          )}

          {(activeTotal > 0 || active.rows.length > 0) && (
            <Section>
              <SectionHead>
                Active remedials<Count>{activeTotal}</Count>
                <SectionHint>Confirmed: turn one into a task to fix it</SectionHint>
              </SectionHead>
              {renderTable(active.rows, 'active')}
              <InfiniteScrollSentinel
                hasMore={!!active.meta && active.meta.currentPage < active.meta.lastPage}
                loading={active.loadingMore}
                onLoadMore={() => load('open', (active.meta?.currentPage ?? 1) + 1)}
              />
              {active.rows.length < activeTotal && (
                <ListTotal>Showing {active.rows.length} of {activeTotal}</ListTotal>
              )}
            </Section>
          )}
        </>
      )}

      <ConvertToTaskDialog
        action={convertTarget ? { ...convertTarget, buildingPassport: convertTarget.buildingPassport || { id: bpId } } : null}
        open={!!convertTarget}
        onClose={() => setConvertTarget(null)}
        onConverted={a => { dropRow('open', a.id); toast.success('Task created. This remedial resolves once the task is completed.') }}
      />
    </div>
  )
}

export default RecommendedRemedialsPanel

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Sub = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  line-height: 1.55;
  margin-bottom: 16px;
  strong { color: ${({ theme }) => theme.colors.haiti}; font-weight: 700; }
`

const Section = styled.div`
  margin-bottom: 22px;
`

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 11.5px;
  font-weight: 800;
`

const SectionHint = styled.span`
  font-size: 11.5px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const EmptyState = styled.div`
  padding: 36px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 14px;
  border: 1px dashed #e4e4e7;
`

const TableScroll = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #ececec;
  border-radius: 12px;
  overflow: auto;
`

const ListTotal = styled.div`
  padding: 10px 14px;
  text-align: center;
  font-size: 11.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  text-align: left;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
  padding: 12px 14px;
  border-bottom: 1px solid #ececec;
  background: ${({ theme }) => theme.colors.lighterGrey};
  white-space: nowrap;
  cursor: ${({ $sortable }) => $sortable ? 'pointer' : 'default'};
  user-select: none;
  &:hover { color: ${({ $sortable, theme }) => $sortable ? theme.colors.haiti : theme.colors.grey[800]}; }
`

const SortLabel = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
`

const Caret = styled.span`
  font-size: 9px;
  opacity: ${({ $active }) => $active ? 1 : 0.4};
  color: ${({ $active, theme }) => $active ? theme.colors.secondary : 'inherit'};
`

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const SearchWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 240px;
  min-width: 200px;
  max-width: 360px;
  padding: 0 10px;
  height: 34px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #e4e4e7;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.grey[800]};
  &:focus-within { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.12); }
`

const SearchInput = styled.input`
  font: inherit;
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  &::placeholder { color: ${({ theme }) => theme.colors.grey[700]}; }
`

const ClearSearch = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; background: none; cursor: pointer; padding: 0;
  color: ${({ theme }) => theme.colors.grey[800]};
  &:hover { color: ${({ theme }) => theme.colors.haiti}; }
`

const SevChips = styled.div`
  display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap;
`

const SevChip = styled.button`
  font: inherit;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 10.5px;
  font-weight: 800;
  padding: 5px 11px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 150ms ease;
  background: ${({ $on, $tint }) => $on ? `${$tint}1A` : '#f4f4f6'};
  color: ${({ $on, $tint, theme }) => $on ? $tint : theme.colors.grey[800]};
  border: 1px solid ${({ $on, $tint }) => $on ? `${$tint}55` : '#e4e4e7'};
  &:hover { border-color: ${({ $tint }) => `${$tint}55`}; }
`

const ClearAll = styled.button`
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 10px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const ClearInline = styled.button`
  font: inherit;
  border: none;
  background: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 700;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`

const Row = styled.tr`
  animation: ${fadeUp} 280ms ease both;
  animation-delay: ${({ $stagger }) => 20 + $stagger * 18}ms;
  &:hover { background: rgba(51,190,204,0.04); }
`

const Td = styled.td`
  padding: 10px 14px;
  border-bottom: 1px solid #f4f4f6;
  vertical-align: top;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
`

const RowTitle = styled.div`
  font-weight: 600;
`

const RowDesc = styled.div`
  margin-top: 3px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
  line-height: 1.45;
`

const SeverityPill = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $tint }) => `${$tint}1A`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
`

const SourceChip = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  background: #f3f3f5;
  color: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 999px;
`

const SourceFile = styled.div`
  font: inherit;
  display: inline-block;
  max-width: 220px;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 11px;
  color: #33becc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover { text-decoration: underline; }
`

const DueCell = styled.div`
  display: inline-flex; flex-direction: column; gap: 2px;
  color: ${({ $overdue }) => $overdue ? RAG_LEVELS.critical.fg : 'inherit'};
  font-weight: ${({ $overdue }) => $overdue ? 700 : 500};
  white-space: nowrap;
`

const OverdueMark = styled.span`
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: ${RAG_LEVELS.critical.fg};
`

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]};
`

const RowBtns = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  white-space: nowrap;
`

const RowBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 4px;
  min-height: 40px;
  padding: 5px 11px;
  background: ${({ $tone, theme }) => $tone === 'task' ? theme.colors.secondary : $tone === 'ok' ? 'rgba(42,166,179,0.10)' : '#f4f4f6'};
  color: ${({ $tone, theme }) => $tone === 'task' ? '#fff' : $tone === 'ok' ? '#2aa6b3' : theme.colors.grey[800]};
  border: 1px solid ${({ $tone, theme }) => $tone === 'task' ? theme.colors.secondary : $tone === 'ok' ? 'rgba(42,166,179,0.32)' : '#e4e4e7'};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.03em;
  cursor: pointer;
  white-space: nowrap;
  transition: all 160ms ease;
  &:hover:not(:disabled) { filter: brightness(0.96); }
  &:disabled { opacity: 0.55; cursor: wait; }
`
